import { IDatabaseColumnModel } from "@kuika/kuika-cl-model";
import axios, { AxiosResponse } from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { authHeadersForFetch } from "../../../auth/fetch-headers";
import {
  IExportManagedDbRowsRequest,
  IManagedDbColumnFilter,
  IManagedDbRow,
  ManagedDbExportFormat,
  TableEditorService
} from "../../../services/table-editor-service";
import { failureMessageOr, type ToastId, toastService } from "../../ui-kit/kuika-toast/toast-service";
import { buildColumnFilters, IColumnFilterMap } from "../column-filters";
import { orderColumnsLikeDataGrip } from "../column-order";
import { IGridSortState } from "../editable-data-grid";
import {
  downloadBlob,
  downloadStreaming,
  downloadText,
  downloadXlsx,
  exportFilename,
  IExportContext,
  rowsToCsv,
  rowsToSqlInserts,
  rowsToSqlUpdates,
  rowsToSqlUpserts
} from "../exporters";
import { IRowContextMenuOption } from "../row-context-menu";
import { TableEditorSqlDialect } from "../table-editor-dialect";
import { buildTableEditorErrorAlert, ITableEditorAlert } from "../table-editor-errors";

// KOA-5857 — Export coordination hook.
//
// Two channels:
//   * Toolbar "Export ▾" → triggerBackendExport — server-side streaming
//     download. Always honors the current filter + sort, page-independent.
//   * Right-click context-menu "Export …" → runExport — client-side
//     export of the rows the parent passes in (current page OR selection).
//
// Hook owns the dropdown-anchor state for the toolbar menu and the
// menu-options definition. The parent owns the "after-export" UI
// concerns (closing the right-click menu, surfacing errors) so the hook
// stays declarative.

export type ExportFormat = ManagedDbExportFormat;

export interface IUseTableEditorExportProps {
  tableName: string;
  tableSchema?: string;
  sqlDialect?: TableEditorSqlDialect;
  primaryKeyColumns: string[];
  // True when the table has at least one column that ISN'T part of
  // the PK. UPDATE export emits `SET <col> = <val>` for every non-PK
  // column; a PK-only table would produce `SET  WHERE ...` (invalid
  // SQL), so the menu disables UPDATE for that shape.
  hasNonPkColumns: boolean;
  sort: IGridSortState | null;
  debouncedFilters: IColumnFilterMap;
  // Server-reported total row count for the active filter. Used by
  // the buffered-export gate to warn the user before non-streaming
  // browsers download large payloads into memory. Defaults to 0
  // (no warning) until the first query response lands.
  totalRowCount: number;
  // Column schema for the active table — used by the buffered-export
  // gate to detect blob types (Image, Signature, FileContent,
  // VarBinary, etc.). Few rows of those can still produce a multi-GB
  // Blob, so the gate trips on a lower row threshold when ANY
  // column is a blob type.
  columns: IDatabaseColumnModel[];
  onExportError: (error: ITableEditorAlert | null) => void;
}

// Buffered-export confirmation thresholds. Firefox / Safari today
// don't expose the File System Access API, so `downloadStreaming`
// returns false and we fall back to an axios + Blob round-trip that
// buffers the full response in tab memory before saving. Above this
// gate, the tab can freeze or OOM mid-download with no recovery
// short of reload.
//
// Two triggers — row count alone misses the most common OOM shape:
//
//   1. Many rows (typical small-row tables). 50,000 rows is roughly:
//        - 5-10 MB for typical CSV.
//        - 20-50 MB for wider tables or XLSX.
//        - 100+ MB for SQL UPDATE / UPSERT exports.
//
//   2. Few rows BUT one or more blob columns (Image / Signature /
//      FileContent / ByteArray / VarBinary / Binary / Bytea). A
//      filtered 200-row export of a `Documents` table with 5 MB
//      attachments produces a 1 GB Blob — well past the danger
//      line — but rowCount alone wouldn't trigger. Drop the row
//      threshold to 200 when ANY column is a blob type so the
//      warning fires before the OOM.
//
// Both numbers are UX gates, not hard limits; users who confirm
// get the same code path as before.
const BUFFERED_EXPORT_CONFIRM_ROW_THRESHOLD: number = 50_000;
const BUFFERED_EXPORT_CONFIRM_BLOB_ROW_THRESHOLD: number = 200;

// Kuika data types whose stored values can be multi-MB. Match the
// blob-cell set used elsewhere (cell-editor.tsx `isBinaryBlobType`,
// AI-fill `COMPLEX_NON_FILLABLE_TYPES`). Kept as a Set so the
// per-column check is O(1).
const BLOB_KUIKA_TYPES: ReadonlySet<string> = new Set<string>([
  "Image",
  "Signature",
  "FileContent",
  "ByteArray",
  "VarBinary",
  "Binary",
  "Bytea"
]);

export interface IExportAnchor {
  x: number;
  y: number;
}

/** How the buffered export's progress toast names the format it is preparing. */
const EXPORT_FORMAT_LABEL: Record<ExportFormat, string> = {
  csv: "CSV",
  xlsx: "Excel (XLSX)",
  insert: "SQL INSERT",
  update: "SQL UPDATE",
  upsert: "SQL UPSERT"
};

// Pending buffered-export confirmation. Non-null when the hook has
// stopped mid-flight on the buffered-fallback gate and is waiting on
// the user's call. Parent renders a confirmation modal driven by this
// state; the modal's buttons call `onProceed` (resume) or `onCancel`
// (drop the export).
export interface IBufferedExportConfirm {
  rowCount: number;
  format: ExportFormat;
  onProceed: () => void;
  onCancel: () => void;
}

export interface IUseTableEditorExportResult {
  exportAnchor: IExportAnchor | null;
  openExportMenu: (e: React.MouseEvent<HTMLElement>) => void;
  closeExportMenu: () => void;
  exportAllMenuOptions: IRowContextMenuOption[];
  // Set when the gate trips on Firefox / Safari with a large export;
  // null otherwise. Parent reads this and renders the confirm modal.
  bufferedExportConfirm: IBufferedExportConfirm | null;
  // True while a server-side export is in flight (after the
  // confirmation modal closes; before the download lands or aborts).
  // Parent disables the Export trigger while this is true. A buffered
  // export (no File System Access API) also says so in a progress
  // toast with a Cancel button, which the hook raises itself.
  isExporting: boolean;
  // Client-side runExport for selection / current-page exports. Parent
  // passes the already-filtered rows (selection vs page) so the hook
  // doesn't need to know about selection. Returns true on success, false
  // when there's nothing to export, throws on writer failure.
  runExport: (format: ExportFormat, rows: IManagedDbRow[], exportColumns: IDatabaseColumnModel[]) => Promise<boolean>;
}

const fallbackExtension = (format: ExportFormat): string => {
  if (format === "insert") {
    return "insert.sql";
  }
  if (format === "update") {
    return "update.sql";
  }
  if (format === "upsert") {
    return "upsert.sql";
  }
  return format;
};

// MIME hint for the OS save dialog. Drives the type grouping the user
// sees in the save panel; the server's Content-Type isn't visible to
// the dialog because the request hasn't gone out yet when the dialog
// opens.
const mimeForFormat = (format: ExportFormat): string => {
  if (format === "csv") {
    return "text/csv";
  }
  if (format === "xlsx") {
    return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  }
  return "application/sql";
};

const filenameFromContentDisposition = (contentDisposition: string | undefined): string | null => {
  if (!contentDisposition) {
    return null;
  }
  const encodedMatch: RegExpExecArray | null = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
  if (encodedMatch?.[1]) {
    try {
      return decodeURIComponent(encodedMatch[1].replace(/^"|"$/g, ""));
    } catch {
      return encodedMatch[1].replace(/^"|"$/g, "");
    }
  }
  const quotedMatch: RegExpExecArray | null = /filename="([^"]+)"/i.exec(contentDisposition);
  if (quotedMatch?.[1]) {
    return quotedMatch[1];
  }
  const bareMatch: RegExpExecArray | null = /filename=([^;]+)/i.exec(contentDisposition);
  if (bareMatch?.[1]) {
    return bareMatch[1].trim();
  }
  return null;
};

export const useTableEditorExport = (props: IUseTableEditorExportProps): IUseTableEditorExportResult => {
  const {
    tableName,
    tableSchema,
    sqlDialect,
    primaryKeyColumns,
    hasNonPkColumns,
    sort,
    debouncedFilters,
    totalRowCount,
    columns,
    onExportError
  } = props;

  // Anchor for the Export-all-rows dropdown — reuses the row context menu
  // component so the dropdown matches every other menu the editor renders.
  const [exportAnchor, setExportAnchor] = useState<IExportAnchor | null>(null);
  // Buffered-export confirm state. Non-null while the hook is paused
  // mid-flight waiting for the user's decision on the big-export
  // warning. See `IBufferedExportConfirm`.
  const [bufferedExportConfirm, setBufferedExportConfirm] = useState<IBufferedExportConfirm | null>(null);
  // In-flight export state. `isExporting` is true between the start
  // of the network call and the download landing (or aborting).
  // The AbortController lets the user cancel from the buffered
  // export's progress toast — feeds both the fetch (streaming path)
  // and Axios (buffered path) requests. Cancelling only fires abort:
  // it never nulls `abortRef.current`, because the in-flight
  // request's finally reads it via an ownership check
  // (`abortRef.current === controller`) before clearing state, and
  // the owner-finally nulls the ref as part of its normal cleanup.
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const abortRef = useRef<AbortController | null>(null);

  // Unmount safety net. If the parent unmounts (table switch,
  // navigation away) while an export is in flight OR the confirm
  // modal is open awaiting a click, fire abort so any awaiting
  // coroutine resolves and lets the Promise + closure be GC'd.
  // Pairs with the abort listener inside the confirm-Promise
  // executor below — the executor's listener is what actually
  // unblocks the awaited Promise.
  useEffect((): (() => void) => {
    return (): void => {
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, []);

  const triggerBackendExport = useCallback(
    async (format: ExportFormat): Promise<void> => {
      setExportAnchor(null);
      onExportError(null);
      // Cancel any in-flight export before starting a new one so a
      // fast double-click on the menu doesn't leave a zombie request.
      abortRef.current?.abort();
      const controller: AbortController = new AbortController();
      abortRef.current = controller;
      setIsExporting(true);
      const filters: IManagedDbColumnFilter[] = buildColumnFilters(debouncedFilters);
      const request: IExportManagedDbRowsRequest = {
        tableSchema,
        format,
        sortColumn: sort?.column,
        sortDir: sort?.dir,
        filters
      };
      const fallbackFilename: string = exportFilename(tableName, fallbackExtension(format));
      try {
        // Try the streaming path first — pipes the server's response
        // body directly to disk via showSaveFilePicker, with zero
        // accumulation in browser memory. Returns false on browsers
        // without the File System Access API (Safari, Firefox today);
        // those fall back to the axios + Blob path below, which has
        // the OOM risk on multi-GB exports but is the only thing
        // those browsers can do without a service-worker shim.
        const url: string = TableEditorService.BuildExportUrl(tableName, request);
        // Mirror the Axios interceptor's headers verbatim — without
        // domainName / currentProjectId / X-ClientId the export
        // endpoint (gated by [KCurrentProjectIdInHeader]) would
        // either reject or run with incomplete context. See
        // `designer/src/auth/fetch-headers.ts` for the canonical set.
        const streamed: boolean = await downloadStreaming({
          url,
          suggestedFilename: fallbackFilename,
          authHeaders: authHeadersForFetch() as Record<string, string>,
          mimeType: mimeForFormat(format),
          signal: controller.signal
        });
        if (streamed) {
          return;
        }
        // Buffered-fallback gate. Firefox / Safari don't have the
        // File System Access API, so the only available path
        // accumulates the whole response in tab memory before
        // saving — multi-MB / multi-GB responses freeze or OOM the
        // tab with no recovery short of reload. Pause and ask before
        // committing to the download.
        //
        // The hook surfaces the pending decision via
        // `bufferedExportConfirm` state; the parent renders a
        // KModal driven by that state and calls onProceed /
        // onCancel. The Promise here resolves when the user
        // answers — true to continue, false to drop the export.
        const hasBlobColumns: boolean = columns.some((c: IDatabaseColumnModel): boolean => {
          return typeof c.kuikaDataType === "string" && BLOB_KUIKA_TYPES.has(c.kuikaDataType);
        });
        // Trip when the table itself is large (50K+ rows of any
        // shape) OR when even a moderate set of rows could carry
        // blob payloads big enough to swamp the buffered fetch.
        // `>=` for the same off-by-one reason as the blob gate
        // below — the constant names the warn-line, not the
        // largest safe count.
        const tripsRowGate: boolean = totalRowCount >= BUFFERED_EXPORT_CONFIRM_ROW_THRESHOLD;
        // `>=` not `>` — the threshold is the smallest row count we
        // want to warn at, not the largest "safe" count. The comment
        // above pins 200 as the warn-line; exclusive `>` made exactly
        // 200 sneak past silently.
        const tripsBlobGate: boolean = hasBlobColumns && totalRowCount >= BUFFERED_EXPORT_CONFIRM_BLOB_ROW_THRESHOLD;
        if (tripsRowGate || tripsBlobGate) {
          const proceed: boolean = await new Promise<boolean>((resolve: (value: boolean) => void): void => {
            // Abort observer — covers two failure modes the original
            // await alone couldn't:
            //   1. Parent unmounts (table switch, navigation) while
            //      the modal is open. Without this, the Promise pins
            //      `columns` / `debouncedFilters` / `controller` in
            //      memory until something GC's the dead component
            //      tree, and the awaited coroutine never returns.
            //   2. Another call to `triggerBackendExport` aborts our
            //      controller. The old code kept us parked on the
            //      user's modal click forever even though the
            //      underlying intent had been cancelled.
            //
            // `finish()` is the single resolve point — it removes the
            // abort listener AND resolves the Promise atomically.
            // Every Proceed / Cancel / abort path routes through it,
            // so the listener is always cleaned up even on the happy
            // paths where the user clicks before the controller
            // aborts. Without explicit removal the listener would
            // outlive the resolved Promise, sitting on the
            // controller for the duration of the buffered fetch —
            // harmless (resolving a settled Promise is a no-op)
            // but cleaner code unhooks what it hooks.
            const onAbort = (): void => finish(false);
            const finish = (value: boolean): void => {
              controller.signal.removeEventListener("abort", onAbort);
              setBufferedExportConfirm(null);
              resolve(value);
            };
            controller.signal.addEventListener("abort", onAbort, { once: true });
            // If the controller was already aborted before we got
            // here (race between two rapid `triggerBackendExport`
            // calls), `addEventListener` won't fire — handle that
            // synchronously. Routes through `finish` too so the
            // (no-op) removeEventListener stays consistent.
            if (controller.signal.aborted) {
              finish(false);
              return;
            }
            setBufferedExportConfirm({
              rowCount: totalRowCount,
              format,
              onProceed: (): void => finish(true),
              onCancel: (): void => finish(false)
            });
          });
          if (!proceed) {
            return;
          }
        }
        // Fallback path — buffered blob. Accepts the memory hit so
        // the user still gets the file on browsers without the
        // File System Access API. It can take many seconds with no
        // sign of life, so one toast carries it from "Preparing" to
        // the download, with a Cancel that aborts THIS export's
        // request (the file is then never delivered). Streaming had
        // no in-tab progress to show (the OS dialog drives it), so the
        // toast only rises on this branch.
        const formatLabel: string = EXPORT_FORMAT_LABEL[format] ?? format.toUpperCase();
        let toastId: ToastId | null = null;
        const download: Promise<void> = (async (): Promise<void> => {
          try {
            const response: AxiosResponse<Blob> = await TableEditorService.ExportRows(
              tableName,
              request,
              controller.signal
            );
            const rawDisposition: unknown =
              response.headers["content-disposition"] ?? response.headers["Content-Disposition"];
            const contentDisposition: string | undefined =
              typeof rawDisposition === "string" ? rawDisposition : undefined;
            const filename: string = filenameFromContentDisposition(contentDisposition) ?? fallbackFilename;
            downloadBlob(filename, response.data);
          } catch (error: unknown) {
            // A failure is said by the editor's own alert (the catch below) and a cancel needs no
            // word at all, so the progress toast steps aside before the rejection reaches it
            // rather than say either a second time.
            if (toastId !== null) toastService.dismiss(toastId);
            throw error;
          }
        })();
        toastId = toastService.promise(
          download,
          {
            loading: `Preparing your ${formatLabel} export…`,
            success: `${formatLabel} export downloaded.`,
            error: failureMessageOr("The export could not be created.")
          },
          { action: { label: "Cancel", onClick: (): void => controller.abort() } }
        );
        await download;
      } catch (error: unknown) {
        // User-driven cancellation isn't an error — quiet success.
        if (
          axios.isCancel(error) ||
          controller.signal.aborted ||
          (error instanceof DOMException && error.name === "AbortError")
        ) {
          return;
        }
        const alert: ITableEditorAlert = await buildTableEditorErrorAlert(error, {
          operation: "export",
          title: "Export failed",
          fallbackMessage: "The export could not be created."
        });
        onExportError(alert);
      } finally {
        // Ownership guard. When a user fires Export twice fast,
        // the FIRST call's catch sees AbortError + returns, then
        // hits this finally — but the SECOND call's setup has
        // already overwritten `abortRef.current` with its own
        // controller AND flipped `isExporting` to its own state. Unconditionally clearing those here
        // would re-enable the export trigger while the SECOND call
        // is still in flight.
        //
        // Only the call whose controller is still the current
        // one owns the cleanup. Identity check covers both
        // halves: the ref clear AND the state flips.
        if (abortRef.current === controller) {
          abortRef.current = null;
          setIsExporting(false);
        }
      }
    },
    [tableName, tableSchema, debouncedFilters, sort, totalRowCount, columns, onExportError]
  );

  const openExportMenu = useCallback((e: React.MouseEvent<HTMLElement>): void => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    // Anchor under the button, left-aligned.
    setExportAnchor({ x: rect.left, y: rect.bottom + 4 });
  }, []);

  const closeExportMenu = useCallback((): void => {
    setExportAnchor(null);
  }, []);

  // Toolbar (Export ▾) — operates server-side on every row matching the
  // current filter + sort, regardless of pagination. Labels start with
  // "all filtered rows" so the scope contrast with the right-click menu
  // (current page / selection) is obvious in passing.
  const exportAllMenuOptions: IRowContextMenuOption[] = [
    { type: "item", label: "Export all filtered rows · CSV", onClick: (): void => void triggerBackendExport("csv") },
    {
      type: "item",
      label: "Export all filtered rows · Excel (XLSX)",
      onClick: (): void => void triggerBackendExport("xlsx")
    },
    { type: "separator" },
    {
      type: "item",
      label: "Export all filtered rows · SQL INSERT",
      onClick: (): void => void triggerBackendExport("insert")
    },
    {
      type: "item",
      label: "Export all filtered rows · SQL UPDATE",
      disabled: primaryKeyColumns.length === 0 || !hasNonPkColumns,
      onClick: (): void => void triggerBackendExport("update")
    },
    {
      type: "item",
      label: "Export all filtered rows · SQL UPSERT (MERGE)",
      disabled: primaryKeyColumns.length === 0,
      onClick: (): void => void triggerBackendExport("upsert")
    }
  ];

  const runExport = useCallback(
    // `exportColumns` (not `columns`) deliberately — the hook itself
    // destructures `columns` from props for the blob-gate check, and
    // shadowing it inside this callback was a refactor trap: anyone
    // reaching for `columns` here would silently capture the wrong
    // value. The parameter and the prop are usually the same
    // reference; the naming separation just keeps the shadow out.
    async (format: ExportFormat, rows: IManagedDbRow[], exportColumns: IDatabaseColumnModel[]): Promise<boolean> => {
      if (rows.length === 0) {
        return false;
      }
      // Match the on-screen ordering (PK → required → nullable). Exporters
      // emit headers and values in this order so what the user sees in the
      // grid is exactly what lands in CSV / XLSX / SQL.
      const orderedColumns = orderColumnsLikeDataGrip(exportColumns, primaryKeyColumns);
      const ctx: IExportContext = {
        tableName,
        tableSchema,
        sqlDialect,
        columns: orderedColumns,
        primaryKeyColumns
      };
      if (format === "csv") {
        downloadText(exportFilename(tableName, "csv"), "text/csv", rowsToCsv(orderedColumns, rows));
      } else if (format === "xlsx") {
        await downloadXlsx(exportFilename(tableName, "xlsx"), orderedColumns, rows);
      } else if (format === "insert") {
        downloadText(exportFilename(tableName, "insert.sql"), "application/sql", rowsToSqlInserts(ctx, rows));
      } else if (format === "update") {
        downloadText(exportFilename(tableName, "update.sql"), "application/sql", rowsToSqlUpdates(ctx, rows));
      } else if (format === "upsert") {
        downloadText(exportFilename(tableName, "upsert.sql"), "application/sql", rowsToSqlUpserts(ctx, rows));
      }
      return true;
    },
    [tableName, tableSchema, sqlDialect, primaryKeyColumns]
  );

  return {
    exportAnchor,
    openExportMenu,
    closeExportMenu,
    exportAllMenuOptions,
    bufferedExportConfirm,
    isExporting,
    runExport
  };
};
